import apiClient from "../api/axiosConfig";
import { ENDPOINTS } from "../api/endpoints";
import {
  ApiResponse,
  ClassificationDeleteByClientPayload,
  ClassificationDeleteByProductPayload,
  ClassificationInsertByClientPayload,
  ClassificationInsertByProductPayload,
  ClientType,
  CupoClienteListData,
  CupoProductoListData,
  ProductType,
} from "../../types/api.types";

/** Body raíz del GET cupos por producto (incluye `data` anidado) */
type CupoProductoListHttpBody = {
  message?: string;
  data?: CupoProductoListData;
  status?: unknown;
  consecutiveNumber?: unknown;
};

/** Query opcional al listar clasificación por producto (tras “Buscar”) */
export type CupoProductoListFilters = {
  code?: string;
  product?: string;
  productType?: string;
};

/** Query opcional al listar clasificación por cliente (tras “Buscar”) */
export type CupoClienteListFilters = {
  cuit?: string;
  comercialName?: string;
};

type CupoClienteListHttpBody = {
  message?: string;
  data?: CupoClienteListData;
  status?: unknown;
  consecutiveNumber?: unknown;
};

export const cupoService = {
  getAllCuposPorCliente: async (
    page: number,
    rowPerPage: number,
    filters?: CupoClienteListFilters,
  ): Promise<CupoClienteListData> => {
    const response = await apiClient.get<CupoClienteListHttpBody>(
      ENDPOINTS.CUPOS.GET_ALL_POR_CLIENTE,
      {
        params: {
          page,
          rowPerPage,
          ...(filters && {
            cuit: filters.cuit ?? "",
            comercialName: filters.comercialName ?? "",
          }),
        },
      },
    );
    const payload = response.data?.data;
    const raw = payload?.classificationResponses;
    const list = Array.isArray(raw) ? raw : [];
    return {
      classificationResponses: list.map((item: any) => ({
        cuit: String(item.cuit ?? "").trim(),
        comercialName: String(item.comercialName ?? "").trim(),
        clientTypeID:
          item.clientTypeID != null
            ? String(item.clientTypeID).trim()
            : undefined,
        tipoCliente: String(item.tipoCliente ?? "").trim(),
        percentage: Number(item.percentage ?? 0),
        clientsClassificationId:
          item.clientsClassificationId != null
            ? String(item.clientsClassificationId).trim()
            : item.clientsClassificationID != null
              ? String(item.clientsClassificationID).trim()
              : undefined,
        clientId:
          item.clientId != null
            ? String(item.clientId).trim()
            : item.clientID != null
              ? String(item.clientID).trim()
              : undefined,
      })),
      total: payload?.total ?? list.length,
    };
  },

  getAllCuposPorProducto: async (
    page: number,
    rowPerPage: number,
    filters?: CupoProductoListFilters,
  ): Promise<CupoProductoListData> => {
    const response = await apiClient.get<CupoProductoListHttpBody>(
      ENDPOINTS.CUPOS.GET_ALL_POR_PRODUCTO,
      {
        params: {
          page,
          rowPerPage,
          ...(filters && {
            code: filters.code ?? "",
            product: filters.product ?? "",
            productType: filters.productType ?? "",
          }),
        },
      },
    );
    const payload = response.data?.data;
    const raw = payload?.classificationResponses;
    const list = Array.isArray(raw) ? raw : [];
    return {
      classificationResponses: list.map((item: any) => ({
        code: String(item.code ?? "").trim(),
        nombre: String(item.nombre ?? "").trim(),
        tipoProducto: String(item.tipoProducto ?? "").trim(),
        percentage: Number(item.percentage ?? 0),
        productClassificationId:
          item.productClassificationId != null
            ? String(item.productClassificationId).trim()
            : item.productClassificationID != null
              ? String(item.productClassificationID).trim()
              : undefined,
        productId:
          item.productId != null
            ? String(item.productId).trim()
            : item.productID != null
              ? String(item.productID).trim()
              : undefined,
        productTypeId:
          item.productTypeId != null
            ? String(item.productTypeId).trim()
            : item.productTypeID != null
              ? String(item.productTypeID).trim()
              : undefined,
      })),
      total: payload?.total ?? list.length,
    };
  },

  getAllTipos: async (): Promise<ProductType[]> => {
    const response = await apiClient.get<ApiResponse<ProductType[]>>(
      ENDPOINTS.CUPOS.GET_ALL_TIPOS,
    );
    const raw = response.data.data ?? response.data;
    const list = Array.isArray(raw) ? raw : [];
    return list.map((item: any) => ({
      productTypeID:
        item.productTypeID ??
        item.productTypeId ??
        item.id ??
        item.tipoId ??
        item,
      name:
        item.name ??
        item.nombre ??
        item.description ??
        String(
          item.productTypeID ??
            item.productTypeId ??
            item.id ??
            item.tipoId ??
            item,
        ),
      description: item.description,
    }));
  },

  getAllTiposCliente: async (): Promise<ClientType[]> => {
    const response = await apiClient.get<ApiResponse<ClientType[]>>(
      ENDPOINTS.CUPOS.GET_ALL_TIPOS_CLIENTE,
    );
    const raw = response.data.data ?? response.data;
    const list = Array.isArray(raw) ? raw : [];
    return list.map((item: any) => ({
      clientTypeID:
        item.clientTypeID ??
        item.clientTypeId ??
        item.id ??
        item.tipoId ??
        item,
      name:
        item.name ??
        item.nombre ??
        item.description ??
        String(
          item.clientTypeID ??
            item.clientTypeId ??
            item.id ??
            item.tipoId ??
            item,
        ),
      description: item.description,
    }));
  },

  insertClassificationByProduct: async (
    payload: ClassificationInsertByProductPayload,
  ): Promise<void> => {
    await apiClient.post(ENDPOINTS.CUPOS.INSERT_BY_PRODUCT, payload);
  },

  insertClassificationByClient: async (
    payload: ClassificationInsertByClientPayload,
  ): Promise<void> => {
    await apiClient.post(ENDPOINTS.CUPOS.INSERT_BY_CLIENT, payload);
  },

  deleteClassificationByProduct: async (
    payload: ClassificationDeleteByProductPayload,
  ): Promise<void> => {
    await apiClient.post(ENDPOINTS.CUPOS.DELETE_BY_PRODUCT, payload);
  },

  deleteClassificationByClient: async (
    payload: ClassificationDeleteByClientPayload,
  ): Promise<void> => {
    await apiClient.post(ENDPOINTS.CUPOS.DELETE_BY_CLIENT, payload);
  },
};
